import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Paper,
  Typography,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Divider,
  CircularProgress,
  Chip,
} from "@mui/material";
import { Assessment, Download as DownloadIcon } from "@mui/icons-material";
import axios from "axios";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import s3Utils from "../../../src/utils/s3Utils";
import { decryptAppId, decryptText } from "../../utils/encryption";
import previewReportService from "../../services/previewReportService";

const MedicalPreviewReport = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [labDetailsState, setlabDetailsState] = useState([]);
  const [basicDetailsState, setBasicDetails] = useState([]);
  const [assessmentTypeid, setassessmentTypeid] = useState("");
  const [disciplineState, setDiscipline] = useState([]);
  const [locationDetailsState, setLocationDetails] = useState([]);
  const [scopeState, setScope] = useState([]);
  const [organisationDetailsState, setOrganisationDetails] = useState([]);
  const [organisationDetailsWithDeletedState, setOrganisationDetailsWithDeleted] = useState([]);
  const [equipmentDetailsState, setEquipmentDetails] = useState([]);
  const [equipmentDetailsWithDeletedState, setEquipmentDetailsWithDeleted] = useState([]);
  const [referenceMaterialsState, setReferenceMaterials] = useState([]);
  const [observationsState, setObservations] = useState([]);
  const [nonAnalyticalInstrumentsState, setNonAnalyticalInstruments] = useState(
    [],
  );
  const [form153PhantomsState, setForm153Phantoms] = useState([]);
  const [radioDetailsState, setRadioDetails] = useState([]);
  const [proficiencyTestingState, setProficiencyTesting] = useState([]);
  const [downloading, setDownloading] = useState(false);
  const [isObservationVisible, setIsObservationVisible] = useState(false);
  const [isAutoDownloading, setIsAutoDownloading] = useState(false);

  useEffect(() => {
    const encryptedAppId = getQueryParam("appId");
    const encryptedOrgId = getQueryParam("orgId");
    const encryptedObservation = getQueryParam("observation");

    if (encryptedAppId && encryptedOrgId && encryptedObservation) {
      try {
        const decryptedAppId = decryptAppId(encryptedAppId);
        const decryptedOrgId = decryptText(encryptedOrgId);
        const decryptedObservation = decryptText(encryptedObservation);

        if (decryptedAppId && decryptedOrgId) {
          fetchForm153Data(decryptedAppId, decryptedOrgId);
        }
        setIsObservationVisible(decryptedObservation === "true");

        console.log(
          "✅ PreviewReport - Parameters successfully decrypted and used",
        );
      } catch (error) {
        console.error("❌ Error decrypting preview report parameters:", error);
        const appId = encryptedAppId;
        const orgId = encryptedOrgId;
        const observation = encryptedObservation;
        if (appId && orgId) {
          fetchForm153Data(appId, orgId);
        }
        setIsObservationVisible(observation === "true");
      }
    }
  }, [location.search]);

  // Handle auto-download functionality
  useEffect(() => {
    const autoDownload = getQueryParam("autoDownload");
    const fromSubmission = getQueryParam("fromSubmission");

    if (
      autoDownload === "true" &&
      fromSubmission === "true" &&
      !isAutoDownloading &&
      labDetailsState.length > 0
    ) {
      console.log("🚀 Auto-downloading PDF after form submission...");
      setIsAutoDownloading(true);

      setTimeout(() => {
        handleAutoDownloadAndSave();
      }, 1000);
    }
  }, [labDetailsState, isAutoDownloading]);

  const getQueryParam = (param) => {
    const params = new URLSearchParams(location.search);
    return params.get(param);
  };

  const fetchForm153Data = async (appId, orgId) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/preview-report/${appId}/${orgId}`,
      );

      if (response.data.success) {
        const {
          labDetails,
          basicDetails,
          discipline,
          locationDetails,
          scope,
          organisationDetails,
          organisationDetailsWithDeleted,
          equipmentDetails,
          equipmentDetailsWithDeleted,
          referenceMaterials,
          observations,
          nonAnalyticalInstruments,
          form153Phantoms,
          form153KitsRadioisotopes,
          proficiencyTestingProviders,
        } = response.data.data;

        setlabDetailsState(labDetails);
        setBasicDetails(basicDetails);
        setassessmentTypeid(basicDetails[0]?.AssessmentTypeId);
        setDiscipline(discipline);
        setLocationDetails(locationDetails);
        setScope(scope);
        setOrganisationDetails(organisationDetails);
        setOrganisationDetailsWithDeleted(organisationDetailsWithDeleted || []);
        setEquipmentDetails(equipmentDetails);
        setEquipmentDetailsWithDeleted(equipmentDetailsWithDeleted || []);
        setReferenceMaterials(referenceMaterials);
        setObservations(observations);
        setNonAnalyticalInstruments(nonAnalyticalInstruments);
        setForm153Phantoms(form153Phantoms);
        setRadioDetails(form153KitsRadioisotopes);
        setProficiencyTesting(proficiencyTestingProviders);
        console.log("assessmentTypeId", basicDetails[0]?.AssessmentTypeId);
      } else {
        console.error("API Error:", response.data.message);
      }
    } catch (error) {
      console.error("Request Failed:", error.message);
    }
  };

  const getObservationBySection = (sectionName, sourceId) => {
    if (sourceId) {
      return observationsState.find(
        (item) =>
          item.SectionName &&
          item.SectionName.toLowerCase() === sectionName.toLowerCase() &&
          item.sourceid === sourceId,
      );
    } else {
      return observationsState.find(
        (item) =>
          item.SectionName &&
          item.SectionName.toLowerCase() === sectionName.toLowerCase(),
      );
    }
  };

  const renderObservationCell = (observationData) => {
    if (!observationData) return "No observation";

    const observation = observationData.Observation;
    const status = observationData.InformationStatus;
    const informationProvided = observationData.InformationProvided;

    const isAdequate =
      observation &&
      observation.trim().toLowerCase() ===
        "document adequacy check completed" &&
      status &&
      status.toLowerCase() === "adequate";

    if (isAdequate) {
      return (
        <div style={{ textAlign: "left" }}>
          <span style={{ color: "green", fontWeight: "bold" }}>Adequate</span>
        </div>
      );
    }

    if (status && status.toLowerCase() === "inadequate") {
      return (
        <div style={{ textAlign: "left" }}>
          <span style={{ color: "red", fontWeight: "bold" }}>
            {informationProvided || observation || "No information provided"}
          </span>
        </div>
      );
    }

    return (
      <div style={{ textAlign: "left" }}>
        <span style={{ color: "black" }}>
          {observation || "No observation"}
        </span>
      </div>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-GB");
    } catch (error) {
      return dateString;
    }
  };

  // Auto-download PDF
  const handleAutoDownloadAndSave = async () => {
    console.log("🎯 Starting auto PDF generation and save process...");

    try {
      const encryptedappId = getQueryParam("appId");
      const encryptedorgId = getQueryParam("orgId");

      const appId = decryptAppId(encryptedappId);
      const orgId = decryptText(encryptedorgId);

      if (!appId || !orgId) {
        console.error("Missing appId or orgId for PDF generation");
        navigate("/main/dashboard");
        return;
      }

      console.log("📄 Auto-generating PDF...");

      const now = new Date();
      const dateStr = `${now.getMonth() + 1}-${now.getDate()}-${now.getFullYear()}`;
      const hours = now.getHours().toString().padStart(2, "0");
      const minutes = now.getMinutes().toString().padStart(2, "0");
      const ampm = hours >= 12 ? "PM" : "AM";
      const hour12 = (hours % 12 || 12).toString().padStart(2, "0");
      const timeStr = `${hour12}-${minutes}${ampm}`;
      const filename = `PreviewReport_${appId}-${dateStr}_${timeStr}.pdf`;

      console.log("🔧 Generating PDF with comprehensive service...");

      const result =
        await previewReportService.generateAndSavePreviewReportAdditional(
          orgId,
          appId,
          3,
          "PreviewReport",
          assessmentTypeid,
          isObservationVisible,
        );

      if (result.success) {
        console.log("✅ PDF generated and saved successfully!");
        console.log("📊 Result:", result);

        setTimeout(() => {
          console.log("🏠 Redirecting to dashboard...");
          navigate("/main/dashboard");
        }, 2000);
      } else {
        console.error("❌ Failed to generate and save PDF:", result.message);
        alert(`Failed to generate PDF: ${result.message}`);
        navigate("/main/dashboard");
      }
    } catch (error) {
      console.error("❌ Error in auto PDF generation and save:", error);
      alert(`Error generating PDF: ${error.message}`);
      navigate("/main/dashboard");
    }
  };

  const labDetail = labDetailsState.length > 0 ? labDetailsState[0] : {};

  const viewS3File = async (fileName) => {
    try {
        if (fileName.includes("live/")) {
         fileName = fileName.replace("live/", "");
       }
      const url = await s3Utils.generateSignedUrlForFilename(fileName);
      if (url) {
        window.open(url, "_blank", "noopener,noreferrer");
      }
    } catch (error) {
      console.error("Error generating S3 URL:", error);
    }
  };

  return Number(assessmentTypeid) === 11 ? (
    // --- AssessmentTypeId 11: Organization Details only ---
    <Box sx={{ p: 2, bgcolor: "#f5f5f5", minHeight: 0, overflow: "hidden" }}>
      <Typography
        variant="h6"
        sx={{ fontWeight: "bold", bgcolor: "#e3f2fd", p: 1, mb: 1 }}
      >
        Organization Details
      </Typography>
      <TableContainer
        component={Paper}
        sx={{
          mb: 3,
          borderRadius: 0,
          maxWidth: "100%",
          mx: "auto",
          overflowX: "auto",
        }}
      >
        <Table
          size="small"
          sx={{
            border: "1px solid #bdbdbd",
            "& th, & td": { border: "1px solid #bdbdbd" },
            borderCollapse: "collapse",
            minWidth: 1000,
          }}
        >
          <TableHead>
            <TableRow sx={{ bgcolor: "#dbd8d8c7" }}>
              <TableCell
                align="center"
                sx={{ minWidth: 50, fontSize: "0.82rem", fontWeight: "bold" }}
              >
                Sr No.
              </TableCell>
              <TableCell
                align="center"
                sx={{ minWidth: 80, fontSize: "0.82rem", fontWeight: "bold" }}
              >
                Discipline
              </TableCell>
              <TableCell
                align="center"
                sx={{ minWidth: 220, fontSize: "0.82rem", fontWeight: "bold" }}
              >
                Personal Information
              </TableCell>
              <TableCell
                align="center"
                sx={{ minWidth: 200, fontSize: "0.82rem", fontWeight: "bold" }}
              >
                Contact Information
              </TableCell>
              <TableCell
                align="center"
                sx={{ minWidth: 220, fontSize: "0.82rem", fontWeight: "bold" }}
              >
                Work Information
              </TableCell>
              {isObservationVisible && (
                <TableCell
                  align="center"
                  sx={{
                    minWidth: 150,
                    fontSize: "0.82rem",
                    fontWeight: "bold",
                  }}
                >
                  Observation
                </TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {organisationDetailsState.length > 0 ? (
              organisationDetailsState
                .filter((staff) => staff.CompetenceAssessmentFile)
                .map((staff, idx) => (
                  <TableRow key={staff.Id}>
                    <TableCell
                      align="center"
                      sx={{ fontSize: "0.82rem", verticalAlign: "top" }}
                    >
                      {idx + 1}
                    </TableCell>
                    <TableCell
                      align="left"
                      sx={{ fontSize: "0.82rem", verticalAlign: "top" }}
                    >
                      {staff.DisciplineName || "-"}
                    </TableCell>
                    <TableCell
                      align="left"
                      sx={{ fontSize: "0.82rem", verticalAlign: "top" }}
                    >
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "4px",
                        }}
                      >
                        <div>
                          <strong>Name:</strong> {staff.Name || "-"}
                        </div>
                        <div>
                          <strong>Designation:</strong>{" "}
                          {staff.Designation || "-"}
                        </div>
                        <div>
                          <strong>Qualification:</strong>{" "}
                          {staff.Qualification || "-"}
                        </div>
                        <div>
                          <strong>Specialization:</strong>{" "}
                          {staff.Specialization || "-"}
                        </div>
                        <div>
                          <strong>Experience:</strong>{" "}
                          {staff.RelevantExperience || "-"} years
                        </div>
                        <div>
                          <strong>Training:</strong>{" "}
                          {staff.RelevantTraining || "-"}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell
                      align="left"
                      sx={{ fontSize: "0.82rem", verticalAlign: "top" }}
                    >
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "4px",
                        }}
                      >
                        <div>
                          <strong>Landline:</strong> {staff.LandlineNo || "-"}
                        </div>
                        <div>
                          <strong>Mobile:</strong> {staff.ContactNo || "-"}
                        </div>
                        <div>
                          <strong>Email:</strong> {staff.EmailID || "-"}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell
                      align="left"
                      sx={{ fontSize: "0.82rem", verticalAlign: "top" }}
                    >
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "4px",
                        }}
                      >
                        <div>
                          <strong>Employee Type:</strong>{" "}
                          {staff.EmployeeType || "-"}
                        </div>
                        <div>
                          <strong>Engagement Type:</strong>{" "}
                          {staff.typeofEngagement || "-"}
                        </div>
                        <div>
                          <strong>Timing:</strong> {staff.Timming || "-"}
                        </div>
                        <div>
                          <strong>Authorized Signatory:</strong>{" "}
                          {staff.IsAuthorisedSignatory ? "Yes" : "No"}
                        </div>
                        {staff.IsAuthorisedSignatory && staff.SignatorySign && (
                          <div>
                            <strong>Signatory File:</strong>{" "}
                            <a
                              href="#"
                              onClick={(e) => {
                                e.preventDefault();
                                viewS3File(staff.SignatorySign);
                              }}
                              style={{
                                cursor: "pointer",
                                color: "#1976d2",
                                textDecoration: "underline",
                              }}
                            >
                              View
                            </a>
                          </div>
                        )}
                        {staff.photo && (
                          <div>
                            <strong>Photo:</strong>{" "}
                            <a
                              href={staff.photo}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{
                                cursor: "pointer",
                                color: "#1976d2",
                                textDecoration: "underline",
                              }}
                            >
                              View
                            </a>
                          </div>
                        )}
                        {staff.TrainingCertificateFile && (
                          <div>
                            <strong>Training Certificate File:</strong>{" "}
                            <a
                              href={staff.TrainingCertificateFile}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{
                                cursor: "pointer",
                                color: "#1976d2",
                                textDecoration: "underline",
                              }}
                            >
                              View
                            </a>
                          </div>
                        )}
                        {staff.AppointmentLetterFile && (
                          <div>
                            <strong>Appointment Letter File:</strong>{" "}
                            <a
                              href={staff.AppointmentLetterFile}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{
                                cursor: "pointer",
                                color: "#1976d2",
                                textDecoration: "underline",
                              }}
                            >
                              View
                            </a>
                          </div>
                        )}
                        {staff.BioDataFile && (
                          <div>
                            <strong>Bio Data File:</strong>{" "}
                            <a
                              href={staff.BioDataFile}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{
                                cursor: "pointer",
                                color: "#1976d2",
                                textDecoration: "underline",
                              }}
                            >
                              View
                            </a>
                          </div>
                        )}
                        {staff.CompetenceAssessmentFile && (
                          <div>
                            <strong>Competence Assessment File:</strong>{" "}
                            <a
                              href={staff.CompetenceAssessmentFile}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{
                                cursor: "pointer",
                                color: "#1976d2",
                                textDecoration: "underline",
                              }}
                            >
                              View
                            </a>
                          </div>
                        )}
                        {staff.ExperienceCertificateFile && (
                          <div>
                            <strong>Experience Certificate File:</strong>{" "}
                            <a
                              href={staff.ExperienceCertificateFile}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{
                                cursor: "pointer",
                                color: "#1976d2",
                                textDecoration: "underline",
                              }}
                            >
                              View
                            </a>
                          </div>
                        )}
                        {staff.QualificationCertificateFile && (
                          <div>
                            <strong>Qualification Certificate File:</strong>{" "}
                            <a
                              href={staff.QualificationCertificateFile}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{
                                cursor: "pointer",
                                color: "#1976d2",
                                textDecoration: "underline",
                              }}
                            >
                              View
                            </a>
                          </div>
                        )}
                      </div>
                    </TableCell>
                    {isObservationVisible && (
                      <TableCell
                        align="center"
                        sx={{ fontSize: "0.82rem", verticalAlign: "top" }}
                      >
                        {renderObservationCell(
                          getObservationBySection("Staff", staff.Id),
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                ))
            ) : (
              <TableRow>
                <TableCell
                  align="center"
                  colSpan={isObservationVisible ? 5 : 4}
                >
                  No staff details specified
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  ) : (
    // --- Main layout for other assessment types (including 13 and 17) ---
    <Box sx={{ p: 2, bgcolor: "#f5f5f5", minHeight: 0, overflow: "hidden" }}>
      <style>{`
          @media print {
            #pdf-report-content {
              page-break-inside: avoid;
            }
            .page-break-before {
              page-break-before: always !important;
              break-before: always !important;
            }
            .page-break-after {
              page-break-after: always !important;
              break-after: always !important;
            }
            table {
              page-break-inside: auto !important;
            }
            tr {
              page-break-inside: avoid !important;
              page-break-after: auto !important;
            }
            td {
              page-break-inside: avoid !important;
            }
            thead {
              display: table-header-group !important;
            }
            tfoot {
              display: table-footer-group !important;
            }
          }
          #pdf-report-content table {
            page-break-inside: auto;
          }
          #pdf-report-content tr {
            page-break-inside: avoid;
            page-break-after: auto;
          }
          #pdf-report-content td {
            page-break-inside: avoid;
          }
        `}</style>

      <div id="pdf-report-content">
        <Paper elevation={3} sx={{ p: 2, mb: 2, mt: 1, borderRadius: 0 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <img
                src="/main/images/v1_9old.png"
                alt="NABL Logo"
                style={{ height: 70, marginRight: 24 }}
              />
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  textAlign: "center",
                }}
              >
                <Typography
                  variant="h7"
                  sx={{ fontWeight: "bold", color: "primary.main", mb: 0.5 }}
                >
                  National Accreditation Board for Testing and Calibration
                  Laboratories
                </Typography>
                <Typography
                  variant="h9"
                  sx={{ color: "text.secondary", fontWeight: "bold" }}
                >
                  Preview Report
                </Typography>
              </Box>
            </Box>
          </Box>
          <Divider sx={{ my: 2 }} />
          <Grid container spacing={1}>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                Name of Laboratory:&nbsp;
                <span style={{ fontWeight: 400 }}>
                  {labDetail.CabName || "-"}
                </span>
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                Contact Person:&nbsp;
                <span style={{ fontWeight: 400 }}>
                  {labDetail.NablContPerson || "-"}
                </span>
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                Mobile:&nbsp;
                <span style={{ fontWeight: 400 }}>
                  {labDetail.ContactPersonMobileNo || "-"}
                </span>
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                Email:&nbsp;
                <span style={{ fontWeight: 400 }}>
                  {labDetail.ContactPersonEmail1 || "-"}
                </span>
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                UserName:&nbsp;
                <span style={{ fontWeight: 400 }}>
                  {(() => {
                    const loginName = Array.isArray(basicDetailsState)
                      ? basicDetailsState[0]?.LoginName
                      : basicDetailsState?.LoginName;
                    return loginName || "-";
                  })()}
                </span>
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                CAB ID:&nbsp;
                <span style={{ fontWeight: 400 }}>
                  {(() => {
                    const ApplicationNo = Array.isArray(basicDetailsState)
                      ? basicDetailsState[0]?.ApplicationNo
                      : basicDetailsState?.ApplicationNo;
                    return ApplicationNo || "-";
                  })()}
                </span>
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                Standard:&nbsp;
                <span style={{ fontWeight: 400 }}>
                  ISO 15189:
                  {(Array.isArray(basicDetailsState)
                    ? basicDetailsState[0]?.standard
                    : basicDetailsState?.standard) || "-"}
                </span>
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                Lab Type:&nbsp;
                <span style={{ fontWeight: 400 }}>
                  {(Array.isArray(basicDetailsState)
                    ? basicDetailsState[0]?.LabType
                    : basicDetailsState?.LabType) || "-"}
                </span>
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                Accreditation Category:&nbsp;
                <span style={{ fontWeight: 400 }}>
                  {(Array.isArray(basicDetailsState)
                    ? basicDetailsState[0]?.Assessmentype
                    : basicDetailsState?.Assessmentype) || "-"}
                </span>
              </Typography>
            </Grid>
          </Grid>
        </Paper>

        {/* Section: Organization Staff Details (for AssessmentTypeId 11 and 12) */}
        {[11, 12].includes(assessmentTypeid) && (
          <>
            {/* Section: Organization Staff Details */}
            <Typography
              variant="h6"
              sx={{ fontWeight: "bold", bgcolor: "#e3f2fd", p: 1, mb: 1 }}
            >
              Organization Details
            </Typography>

            {/* Color Legend for Organization */}
            <Box sx={{ display: "flex", gap: 1, mb: 1, flexWrap: "wrap" }}>
              <Chip
                label="New (Blue)"
                size="small"
                sx={{ bgcolor: "#bbdefb", color: "#0d47a1" }}
              />
              <Chip
                label="Updated (Orange)"
                size="small"
                sx={{ bgcolor: "#ffe0b2", color: "#e65100" }}
              />
              <Chip
                label="Deleted (Red)"
                size="small"
                sx={{ bgcolor: "#ffcdd2", color: "#b71c1c" }}
              />
              <Chip
                label="Existing (White/Gray)"
                size="small"
                sx={{ bgcolor: "#f5f5f5", color: "#333" }}
              />
            </Box>

            <TableContainer
              component={Paper}
              sx={{
                mb: 3,
                borderRadius: 0,
                maxWidth: "100%",
                mx: "auto",
                overflowX: "auto",
              }}
            >
              <Table
                size="small"
                sx={{
                  border: "1px solid #bdbdbd",
                  "& th, & td": { border: "1px solid #bdbdbd" },
                  borderCollapse: "collapse",
                  minWidth: 1100,
                }}
              >
                <TableHead>
                  <TableRow sx={{ bgcolor: "#dbd8d8c7" }}>
                    <TableCell
                      align="center"
                      sx={{
                        minWidth: 50,
                        fontSize: "0.82rem",
                        fontWeight: "bold",
                      }}
                    >
                      Sr No.
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        minWidth: 80,
                        fontSize: "0.82rem",
                        fontWeight: "bold",
                      }}
                    >
                      Discipline
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        minWidth: 220,
                        fontSize: "0.82rem",
                        fontWeight: "bold",
                      }}
                    >
                      Personal Information
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        minWidth: 200,
                        fontSize: "0.82rem",
                        fontWeight: "bold",
                      }}
                    >
                      Contact Information
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        minWidth: 220,
                        fontSize: "0.82rem",
                        fontWeight: "bold",
                      }}
                    >
                      Work Information
                    </TableCell>
                    {isObservationVisible && (
                      <TableCell
                        align="center"
                        sx={{
                          minWidth: 150,
                          fontSize: "0.82rem",
                          fontWeight: "bold",
                        }}
                      >
                        Observation
                      </TableCell>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {organisationDetailsWithDeletedState.length > 0 ? (
                    // Apply filter only for assessmentTypeid 11 (as per original logic)
                    (Number(assessmentTypeid) === 11
                      ? organisationDetailsWithDeletedState.filter(
                          (staff) => staff.CompetenceAssessmentFile,
                        )
                      : organisationDetailsWithDeletedState
                    ).map((staff, idx) => {
                      const color = staff.color || "Black";
                      // Define row style based on color
                      let rowStyle = {};
                      if (color === "Blue") {
                        rowStyle = {
                          backgroundColor: "#bbdefb !important",
                          color: "#0d47a1",
                        };
                      } else if (color === "Orange") {
                        rowStyle = {
                          backgroundColor: "#ffe0b2 !important",
                          color: "#e65100",
                        };
                      } else if (color === "Red") {
                        rowStyle = {
                          backgroundColor: "#ffcdd2 !important",
                          color: "#b71c1c",
                        };
                      } else {
                        rowStyle = {
                          backgroundColor:
                            idx % 2 === 0 ? "#ffffff" : "#f8fafc",
                        };
                      }

                      return (
                        <TableRow key={staff.Id} sx={rowStyle}>
                          <TableCell
                            align="center"
                            sx={{ fontSize: "0.82rem", verticalAlign: "top" }}
                          >
                            {idx + 1}
                          </TableCell>
                          <TableCell
                            align="left"
                            sx={{ fontSize: "0.82rem", verticalAlign: "top" }}
                          >
                            {staff.DisciplineName || "-"}
                          </TableCell>
                          <TableCell
                            align="left"
                            sx={{ fontSize: "0.82rem", verticalAlign: "top" }}
                          >
                            <div
                              style={{
                                display: "flex",
                                flexDirection: "column",
                                gap: "4px",
                              }}
                            >
                              <div>
                                <strong>Name:</strong> {staff.Name || "-"}
                              </div>
                              <div>
                                <strong>Designation:</strong>{" "}
                                {staff.Designation || "-"}
                              </div>
                              <div>
                                <strong>Qualification:</strong>{" "}
                                {staff.Qualification || "-"}
                              </div>
                              <div>
                                <strong>Specialization:</strong>{" "}
                                {staff.Specialization || "-"}
                              </div>
                              <div>
                                <strong>Experience:</strong>{" "}
                                {staff.RelevantExperience || "-"} years
                              </div>
                              <div>
                                <strong>Training:</strong>{" "}
                                {staff.RelevantTraining || "-"}
                              </div>
                            </div>
                          </TableCell>
                          <TableCell
                            align="left"
                            sx={{ fontSize: "0.82rem", verticalAlign: "top" }}
                          >
                            <div
                              style={{
                                display: "flex",
                                flexDirection: "column",
                                gap: "4px",
                              }}
                            >
                              <div>
                                <strong>Landline:</strong>{" "}
                                {staff.LandlineNo || "-"}
                              </div>
                              <div>
                                <strong>Mobile:</strong>{" "}
                                {staff.ContactNo || "-"}
                              </div>
                              <div>
                                <strong>Email:</strong> {staff.EmailID || "-"}
                              </div>
                            </div>
                          </TableCell>
                          <TableCell
                            align="left"
                            sx={{ fontSize: "0.82rem", verticalAlign: "top" }}
                          >
                            <div
                              style={{
                                display: "flex",
                                flexDirection: "column",
                                gap: "4px",
                              }}
                            >
                              <div>
                                <strong>Employee Type:</strong>{" "}
                                {staff.EmployeeType || "-"}
                              </div>
                              <div>
                                <strong>Engagement Type:</strong>{" "}
                                {staff.typeofEngagement || "-"}
                              </div>
                              <div>
                                <strong>Timing:</strong> {staff.Timming || "-"}
                              </div>
                              <div>
                                <strong>Authorized Signatory:</strong>{" "}
                                {staff.IsAuthorisedSignatory ? "Yes" : "No"}
                              </div>
                              {staff.IsAuthorisedSignatory &&
                                staff.SignatorySign && (
                                  <div>
                                    <strong>Signatory File:</strong>{" "}
                                    <a
                                      href="#"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        viewS3File(staff.SignatorySign);
                                      }}
                                      style={{
                                        cursor: "pointer",
                                        color: "#1976d2",
                                        textDecoration: "underline",
                                      }}
                                    >
                                      View
                                    </a>
                                  </div>
                                )}
                              {staff.photo && (
                                <div>
                                  <strong>Photo:</strong>{" "}
                                  <a
                                    href={staff.photo}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{
                                      cursor: "pointer",
                                      color: "#1976d2",
                                      textDecoration: "underline",
                                    }}
                                  >
                                    View
                                  </a>
                                </div>
                              )}
                              {staff.TrainingCertificateFile && (
                                <div>
                                  <strong>Training Certificate File:</strong>{" "}
                                  <a
                                    href={staff.TrainingCertificateFile}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{
                                      cursor: "pointer",
                                      color: "#1976d2",
                                      textDecoration: "underline",
                                    }}
                                  >
                                    View
                                  </a>
                                </div>
                              )}
                              {staff.AppointmentLetterFile && (
                                <div>
                                  <strong>Appointment Letter File:</strong>{" "}
                                  <a
                                    href={staff.AppointmentLetterFile}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{
                                      cursor: "pointer",
                                      color: "#1976d2",
                                      textDecoration: "underline",
                                    }}
                                  >
                                    View
                                  </a>
                                </div>
                              )}
                              {staff.BioDataFile && (
                                <div>
                                  <strong>Bio Data File:</strong>{" "}
                                  <a
                                    href={staff.BioDataFile}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{
                                      cursor: "pointer",
                                      color: "#1976d2",
                                      textDecoration: "underline",
                                    }}
                                  >
                                    View
                                  </a>
                                </div>
                              )}
                              {staff.CompetenceAssessmentFile && (
                                <div>
                                  <strong>Competence Assessment File:</strong>{" "}
                                  <a
                                    href={staff.CompetenceAssessmentFile}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{
                                      cursor: "pointer",
                                      color: "#1976d2",
                                      textDecoration: "underline",
                                    }}
                                  >
                                    View
                                  </a>
                                </div>
                              )}
                              {staff.ExperienceCertificateFile && (
                                <div>
                                  <strong>Experience Certificate File:</strong>{" "}
                                  <a
                                    href={staff.ExperienceCertificateFile}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{
                                      cursor: "pointer",
                                      color: "#1976d2",
                                      textDecoration: "underline",
                                    }}
                                  >
                                    View
                                  </a>
                                </div>
                              )}
                              {staff.QualificationCertificateFile && (
                                <div>
                                  <strong>
                                    Qualification Certificate File:
                                  </strong>{" "}
                                  <a
                                    href={staff.QualificationCertificateFile}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{
                                      cursor: "pointer",
                                      color: "#1976d2",
                                      textDecoration: "underline",
                                    }}
                                  >
                                    View
                                  </a>
                                </div>
                              )}
                            </div>
                          </TableCell>
                          {isObservationVisible && (
                            <TableCell
                              align="center"
                              sx={{ fontSize: "0.82rem", verticalAlign: "top" }}
                            >
                              {renderObservationCell(
                                getObservationBySection("Staff", staff.Id),
                              )}
                            </TableCell>
                          )}
                        </TableRow>
                      );
                    })
                  ) : (
                    <TableRow>
                      <TableCell
                        align="center"
                        colSpan={isObservationVisible ? 6 : 5}
                      >
                        No staff details specified
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}

        {/* ========== Section: Equipment Details (using equipmentDetailsWithDeleted) ========== */}
        {[13, 17].includes(assessmentTypeid) && (
          <>
            {/* Section: Equipment Details */}
            <Typography
              variant="h6"
              sx={{ fontWeight: "bold", bgcolor: "#e3f2fd", p: 1, mb: 1 }}
            >
              Equipment Details
            </Typography>

            {/* Color Legend */}
            <Box sx={{ display: "flex", gap: 1, mb: 1, flexWrap: "wrap" }}>
              <Chip
                label="New (Blue)"
                size="small"
                sx={{ bgcolor: "#bbdefb", color: "#0d47a1" }}
              />
              <Chip
                label="Updated (Orange)"
                size="small"
                sx={{ bgcolor: "#ffe0b2", color: "#e65100" }}
              />
              <Chip
                label="Deleted (Red)"
                size="small"
                sx={{ bgcolor: "#ffcdd2", color: "#b71c1c" }}
              />
              <Chip
                label="Existing (White/Gray)"
                size="small"
                sx={{ bgcolor: "#f5f5f5", color: "#333" }}
              />
            </Box>

            <TableContainer
              component={Paper}
              sx={{
                mb: 3,
                borderRadius: 0,
                maxWidth: "100%",
                mx: "auto",
                overflowX: "auto",
              }}
            >
              <Table
                size="small"
                sx={{
                  border: "1px solid #bdbdbd",
                  "& th, & td": { border: "1px solid #bdbdbd" },
                  borderCollapse: "collapse",
                  minWidth: 1100,
                }}
              >
                <TableHead>
                  <TableRow sx={{ bgcolor: "#dbd8d8c7" }}>
                    <TableCell
                      align="center"
                      sx={{
                        minWidth: 50,
                        fontSize: "0.82rem",
                        fontWeight: "bold",
                      }}
                    >
                      Sr No.
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        minWidth: 80,
                        fontSize: "0.82rem",
                        fontWeight: "bold",
                      }}
                    >
                      Discipline
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        minWidth: 220,
                        fontSize: "0.82rem",
                        fontWeight: "bold",
                      }}
                    >
                      Equipment Information
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        minWidth: 220,
                        fontSize: "0.82rem",
                        fontWeight: "bold",
                      }}
                    >
                      Technical Specifications
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        minWidth: 240,
                        fontSize: "0.82rem",
                        fontWeight: "bold",
                      }}
                    >
                      Calibration Information
                    </TableCell>
                    {/* Extra columns for AssessmentTypeId 17 */}
                    {Number(assessmentTypeid) === 17 && (
                      <TableCell
                        align="center"
                        sx={{
                          minWidth: 240,
                          fontSize: "0.82rem",
                          fontWeight: "bold",
                        }}
                      >
                        Equipment Change Information
                      </TableCell>
                    )}
                    {isObservationVisible && (
                      <TableCell
                        align="center"
                        sx={{
                          minWidth: 150,
                          fontSize: "0.82rem",
                          fontWeight: "bold",
                        }}
                      >
                        Observation
                      </TableCell>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {equipmentDetailsWithDeletedState.length > 0 ? (
                    equipmentDetailsWithDeletedState.map((equipment, idx) => {
                      const color = equipment.color || "Black";
                      // Define row style based on color
                      let rowStyle = {};
                      if (color === "Blue") {
                        rowStyle = {
                          backgroundColor: "#bbdefb !important",
                          color: "#0d47a1",
                        };
                      } else if (color === "Orange") {
                        rowStyle = {
                          backgroundColor: "#ffe0b2 !important",
                          color: "#e65100",
                        };
                      } else if (color === "Red") {
                        rowStyle = {
                          backgroundColor: "#ffcdd2 !important",
                          color: "#b71c1c",
                        };
                      } else {
                        rowStyle = {
                          backgroundColor:
                            idx % 2 === 0 ? "#ffffff" : "#f8fafc",
                        };
                      }

                      return (
                        <TableRow key={equipment.Id} sx={rowStyle}>
                          <TableCell
                            align="center"
                            sx={{ fontSize: "0.82rem", verticalAlign: "top" }}
                          >
                            {idx + 1}
                          </TableCell>
                          <TableCell
                            align="left"
                            sx={{ fontSize: "0.82rem", verticalAlign: "top" }}
                          >
                            {equipment.DisciplineName || "-"}
                          </TableCell>
                          <TableCell
                            align="left"
                            sx={{ fontSize: "0.82rem", verticalAlign: "top" }}
                          >
                            <div
                              style={{
                                display: "flex",
                                flexDirection: "column",
                                gap: "4px",
                              }}
                            >
                              <div>
                                <strong>Equipment Name:</strong>{" "}
                                {equipment.NameOfEquipment || "-"}
                              </div>
                              <div>
                                <strong>Equipment ID:</strong>{" "}
                                {equipment.Equipmentid || "-"}
                              </div>
                              <div>
                                <strong>UID:</strong> {equipment.Uid || "-"}
                              </div>
                              <div>
                                <strong>Model:</strong> {equipment.Model || "-"}
                              </div>
                              <div>
                                <strong>Serial No:</strong>{" "}
                                {equipment.SerialNo || "-"}
                              </div>
                            </div>
                          </TableCell>
                          <TableCell
                            align="left"
                            sx={{ fontSize: "0.82rem", verticalAlign: "top" }}
                          >
                            <div
                              style={{
                                display: "flex",
                                flexDirection: "column",
                                gap: "4px",
                              }}
                            >
                              <div>
                                <strong>Manufacturer:</strong>{" "}
                                {equipment.MakeCompany || "-"}
                              </div>
                              <div>
                                <strong>Year of Make:</strong>{" "}
                                {equipment.YearOfMake || "-"}
                              </div>
                              <div>
                                <strong>Receipt Date:</strong>{" "}
                                {formatDate(equipment.ReceiptDate) || "-"}
                              </div>
                              <div>
                                <strong>Installation Date:</strong>{" "}
                                {formatDate(equipment.DateOfInstallation) ||
                                  "-"}
                              </div>
                              <div>
                                <strong>Limit of Detection:</strong>{" "}
                                {equipment.LimitOfDetection || "-"}
                              </div>
                            </div>
                          </TableCell>
                          <TableCell
                            align="left"
                            sx={{ fontSize: "0.82rem", verticalAlign: "top" }}
                          >
                            <div
                              style={{
                                display: "flex",
                                flexDirection: "column",
                                gap: "4px",
                              }}
                            >
                              <div>
                                <strong>Calibration Date:</strong>{" "}
                                {formatDate(equipment.DateOfLastCalibration) ||
                                  "-"}
                              </div>
                              <div>
                                <strong>Calibration Due:</strong>{" "}
                                {formatDate(equipment.CalibrationDueOn) || "-"}
                              </div>
                              <div>
                                <strong>Calibrated By:</strong>{" "}
                                {equipment.CalibratedByType || "-"}
                              </div>
                              <div>
                                <strong>Calibration Agency:</strong>{" "}
                                {equipment.CalibratedByType === "In-house"
                                  ? equipment.InHouseCalibration || "-"
                                  : equipment.ExternalCalibrationAgency || "-"}
                              </div>
                              <div>
                                <strong>Traceability:</strong>{" "}
                                {equipment.Traceability ||
                                  equipment.InHouseTraceability ||
                                  equipment.InHouseCalibration ||
                                  "-"}
                              </div>
                            </div>
                          </TableCell>
                          {/* Extra columns for AssessmentTypeId 17 */}
                          {Number(assessmentTypeid) === 17 && (
                            <>
                              <TableCell
                                align="left"
                                sx={{
                                  fontSize: "0.82rem",
                                  verticalAlign: "top",
                                }}
                              >
                                <div
                                  style={{
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: "4px",
                                  }}
                                >
                                  <div>
                                    <strong>Method Change:</strong>{" "}
                                    {equipment.methodChange || "-"}
                                  </div>
                                  <div>
                                    <strong>Method Change Details:</strong>{" "}
                                    {equipment.methodChangeDetails || "-"}
                                  </div>
                                  <div>
                                    <strong>Equipment Type:</strong>{" "}
                                    {equipment.equipmentType || "-"}
                                  </div>
                                  <div>
                                    <strong>Replacement UID:</strong>{" "}
                                    {equipment.replacementUID || "-"}
                                  </div>
                                  <div>
                                    <strong>Compare Report:</strong>{" "}
                                    {equipment.compareReport ? (
                                      <a
                                        href="#"
                                        onClick={(e) => {
                                          e.preventDefault();
                                          viewS3File(equipment.compareReport);
                                        }}
                                        style={{
                                          cursor: "pointer",
                                          color: "#1976d2",
                                          textDecoration: "underline",
                                        }}
                                      >
                                        View
                                      </a>
                                    ) : (
                                      "-"
                                    )}
                                  </div>
                                  <div>
                                    <strong>Method Validation Report:</strong>{" "}
                                    {equipment.methodValidationReport ? (
                                      <a
                                        href="#"
                                        onClick={(e) => {
                                          e.preventDefault();
                                          viewS3File(
                                            equipment.methodValidationReport,
                                          );
                                        }}
                                        style={{
                                          cursor: "pointer",
                                          color: "#1976d2",
                                          textDecoration: "underline",
                                        }}
                                      >
                                        View
                                      </a>
                                    ) : (
                                      "-"
                                    )}
                                  </div>
                                </div>
                              </TableCell>
                            </>
                          )}
                          {isObservationVisible && (
                            <TableCell
                              align="center"
                              sx={{ fontSize: "0.82rem", verticalAlign: "top" }}
                            >
                              {renderObservationCell(
                                getObservationBySection(
                                  "Equipment",
                                  `${equipment.Id}`,
                                ),
                              )}
                            </TableCell>
                          )}
                        </TableRow>
                      );
                    })
                  ) : (
                    <TableRow>
                      <TableCell
                        align="center"
                        colSpan={
                          isObservationVisible
                            ? Number(assessmentTypeid) === 17
                              ? 11
                              : 5
                            : Number(assessmentTypeid) === 17
                              ? 10
                              : 4
                        }
                      >
                        No equipment details specified
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}

        {/* Declaration Section - unchanged */}
        <Typography
          variant="h5"
          sx={{
            fontWeight: "bold",
            bgcolor: "#e3f2fd",
            p: 2,
            mb: 2,
            mt: 4,
            textAlign: "center",
          }}
        >
          DECLARATION
        </Typography>

        <Typography variant="h6" sx={{ fontWeight: "bold", mb: 2 }}>
          Terms & Conditions
        </Typography>
        <Typography
          variant="body2"
          sx={{ mb: 2, lineHeight: 1.6, textAlign: "justify" }}
        >
          The terms and conditions mentioned in this document is considered as
          accreditation agreement between NABL and Conformity Assessment Body
          (CAB). By accepting these terms and conditions, it is implied that a
          CAB as an applicant and after obtaining accreditation agrees to comply
          at all times with all Terms and Conditions for NABL Accreditation. Any
          violation of this terms and conditions (Accreditation agreement) shall
          result in adverse decision as per NABL 216 'Procedures for Dealing
          with Adverse Decisions' including legal action.
        </Typography>
        <Typography
          variant="body2"
          sx={{ mb: 2, lineHeight: 1.6, textAlign: "justify" }}
        >
          The Conformity Assessment Body {"{"}Testing Laboratory/ Calibration
          Laboratory/ Medical Testing Laboratory/ Proficiency Testing Provider
          (PTP)/ Reference Material Producer (RMP){"}"} that are applicant or
          accredited by NABL shall be required to fulfill the following terms
          and conditions:
        </Typography>
        <Typography
          variant="body2"
          sx={{ mb: 2, lineHeight: 1.6, textAlign: "justify" }}
        >
          By accepting these terms and conditions, it is implied that a CAB as
          an applicant and after obtaining accreditation agrees to comply at all
          times with all Terms and Conditions for Obtaining and Maintaining NABL
          Accreditation. Any violation of this terms and conditions
          (arrangement) shall result in adverse decision as per NABL 216
          'Procedures for Dealing with Adverse Decisions' including legal
          action.
        </Typography>
        <Typography
          variant="body2"
          sx={{ mb: 3, lineHeight: 1.6, textAlign: "justify" }}
        >
          Dispute, if any, arising out of NABL decisions that remain unresolved
          through mechanism provided by NABL are subject to the exclusive
          jurisdiction of the Courts at New Delhi and none other.
        </Typography>

        <Typography variant="h6" sx={{ fontWeight: "bold", mb: 2 }}>
          Declaration by the Laboratory
        </Typography>
        <Typography variant="body2" sx={{ mb: 2, fontWeight: "bold" }}>
          We declare that:
        </Typography>

        <Box component="ol" sx={{ pl: 2, mb: 2 }}>
          <Typography
            component="li"
            variant="body2"
            sx={{ mb: 1, lineHeight: 1.6, textAlign: "justify" }}
          >
            We are familiar with the Terms & Conditions for Obtaining and
            Maintaining NABL Accreditation (NABL 131), which is enclosed and
            will abide by it. We have understood the requirements given in NABL
            133.
          </Typography>
          <Typography
            component="li"
            variant="body2"
            sx={{ mb: 1, lineHeight: 1.6, textAlign: "justify" }}
          >
            We will maintain strict confidentiality of personal sensitive data.
            This information will not be shared with any other person, agency or
            organization (unless required by law).
          </Typography>
          <Typography
            component="li"
            variant="body2"
            sx={{ mb: 1, lineHeight: 1.6, textAlign: "justify" }}
          >
            We have declared all sources of samples.
          </Typography>
          <Typography
            component="li"
            variant="body2"
            sx={{ mb: 1, lineHeight: 1.6, textAlign: "justify" }}
          >
            We have declared and submitted accurate and complete information in
            the enclosed "Declaration Form"
          </Typography>
          <Typography
            component="li"
            variant="body2"
            sx={{ mb: 1, lineHeight: 1.6, textAlign: "justify" }}
          >
            We have conducted internal audit of our Sample Collection Centre(s)/
            facility(ies) at least once during the last one year.
          </Typography>
          <Typography
            component="li"
            variant="body2"
            sx={{ mb: 1, lineHeight: 1.6, textAlign: "justify" }}
          >
            We agree to comply fully with ISO 15189: 2012 or ISO 15189: 2022 and
            relevant specific criteria for the accreditation of testing
            laboratory and associated Sample Collection Centre(s)/facility(ies).
          </Typography>
          <Typography
            component="li"
            variant="body2"
            sx={{ mb: 1, lineHeight: 1.6, textAlign: "justify" }}
          >
            We agree to comply with accreditation procedures, pay all costs for
            pre-assessment (if applicable), assessment, supplementary visit (if
            any), surveillance and reassessment irrespective of the result.
          </Typography>
          <Typography
            component="li"
            variant="body2"
            sx={{ mb: 1, lineHeight: 1.6, textAlign: "justify" }}
          >
            We agree to co-operate with the assessment team appointed by NABL
            for examination of all relevant documents/records by them and their
            visits to those parts of the laboratory that are part of the scope
            of accreditation.
          </Typography>
          <Typography
            component="li"
            variant="body2"
            sx={{ mb: 1, lineHeight: 1.6, textAlign: "justify" }}
          >
            We comply to all national, regional and local regulatory
            requirements for operating a laboratory.
          </Typography>
          <Typography
            component="li"
            variant="body2"
            sx={{ mb: 1, lineHeight: 1.6, textAlign: "justify" }}
          >
            All information provided in this application is true.
          </Typography>
          <Typography
            component="li"
            variant="body2"
            sx={{ mb: 1, lineHeight: 1.6, textAlign: "justify" }}
          >
            Self-declaration shall be submitted by the laboratory on the basis
            of the internal audit conducted by them, to confirm to NABL that
            their Sample Collection Centre(s)/ facility(ies) are complying with
            NABL norms and relevant clauses of ISO 15189.
          </Typography>
        </Box>
        <Box sx={{ mb: 3 }}>
          <Typography
            variant="body2"
            sx={{ mb: 4, lineHeight: 1.6, textAlign: "justify" }}
          >
            {labDetail.DeclNameOrg || "The authorized representative of CAB"},
            authorised representative of CAB has filled/ verified the contents
            of the application and accepted the above mentioned terms and
            conditions on behalf of CAB.
          </Typography>
        </Box>
        <Box>
          <Typography
            variant="body2"
            sx={{ mb: 4, lineHeight: 1.6, textAlign: "justify" }}
          >
            I have read and understood all the terms and conditions mentioned
            above. *
          </Typography>
          <Typography
            variant="body2"
            sx={{ mb: 4, lineHeight: 1.6, textAlign: "justify" }}
          >
            By accepting the above terms & conditions, it is implied that a CAB/
            Sample Collection Centre/ Facility (ies) declared by medical CAB as
            an applicant and after obtaining accreditation agrees to comply at
            all times with all Terms and Conditions for NABL Accreditation. Any
            violation of this terms and conditions shall result in adverse
            decision such as abeyance, suspension, forced withdrawal etc against
            the CAB as specified in 'NABL 216'. *
          </Typography>
        </Box>
      </div>
    </Box>
  );
};

export default MedicalPreviewReport;
